import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Historical,
  KeyValue,
  Metadata,
  Query,
  SimpleTableRow,
  SortDirection,
  TableHeader,
} from "../../interfaces";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import LinkComponent from "../LinkComponent";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import {
  appHeaderTitle,
  fileNameFormat,
  getExpandState,
} from "../../common/services/Utilities";
import useSortProperties from "../../hooks/useSortProperties";
import { buildQueryUrl, fetchData } from "../../common/services/ApiClient";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import ResultPagination from "../../containers/ResultPagination";
import {
  CBFinancialFilterOptions,
  CBStructureFilterOptions,
  SIFinancialFilterOptions,
  SIStructureFilterOptions,
} from "./FieldSelectionOptions";
import { ApiBaseUrl } from "../../config";
import HistoricalSearchForm from "../../containers/HistoricalSearchForm";
import ApiDataDownload, {
  apiDownloadProps,
  DownloadHeaders,
} from "../ApiDataDownload";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const PREFIX = "HistoricalBankData";

const classes = {
  tableStyles: `${PREFIX}-tableStyles`,
  buttonStyle: `${PREFIX}-buttonStyle`,
  footer: `${PREFIX}-footer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.tableStyles}`]: {
    "& #multi-header-table": {
      "marginTop": 0,
      "& #multi-header-table-head": {
        backgroundColor: "#003256",
      },
    },
    "& #tableWrapper": {
      marginTop: 8,
    },
    "& th": {
      zIndex: 5,
      position: "sticky",
      top: 0,
    },
    "& tr > th#year-header-0": {
      "position": "sticky",
      "left": 0,
      "zIndex": 20,
      "backgroundColor": "#003256",
      "& span": {
        width: 49,
      },
    },
    "& tr > th#locations-header-1": {
      position: "sticky",
      left: 65,
      zIndex: 20,
      backgroundColor: "#003256",
    },
    "& tr:nth-child(odd) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D2E1EE",
    },
    "& tr:nth-child(even) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D9E8F6",
    },
    "& tr:nth-child(odd) > td:nth-child(2)": {
      position: "sticky",
      left: 65,
      zIndex: 10,
      backgroundColor: "#D2E1EE",
    },
    "& tr:nth-child(even) > td:nth-child(2)": {
      position: "sticky",
      left: 65,
      zIndex: 10,
      backgroundColor: "#D9E8F6",
    },
  },

  [`& .${classes.footer}`]: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },

  [`& .${classes.buttonStyle}`]: {
    "color": theme.palette.primary.main,
    "border": "0px solid",
    "borderRadius": "3px",
    "padding": 0,
    "height": "auto",
    "textDecoration": "underline",
    "&:focus, &:hover": {
      color: "#BB3300",
      backgroundColor: "#E6F3FF",
      textDecoration: "underline",
    },
    "&:active": {
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
}));

type HistoricalApiField = keyof Historical;

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateEndDate: (year: string) => void;
}

interface HistoricalHeaders {
  title: string;
  apiField: HistoricalApiField;
}

const staticHeaders: HistoricalHeaders[] = [
  { title: "Year", apiField: "YEAR" },
  { title: "Locations", apiField: "STNAME" },
];

const CBSHeaders = [].concat.apply(
  [],
  CBStructureFilterOptions.map((report) =>
    report.fields.map((field) => {
      return { title: field.label, apiField: field.value };
    }),
  ) as never[],
);
const CBFHeaders = [].concat.apply(
  [],
  CBFinancialFilterOptions.map((report) =>
    report.fields.map((field) => {
      return {
        title: `${field.label}${field.thousandsUS ? "*" : ""}`,
        apiField: field.value,
        thousandsUS: field.thousandsUS,
      };
    }),
  ) as never[],
);
const SISHeaders = [].concat.apply(
  [],
  SIStructureFilterOptions.map((report) =>
    report.fields.map((field) => {
      return {
        title: `${field.label}${field.specialNote ? "**" : ""}`,
        apiField: field.value,
        specialNote: field.specialNote,
      };
    }),
  ) as never[],
);
const SIFHeaders = [].concat.apply(
  [],
  SIFinancialFilterOptions.map((report) =>
    report.fields.map((field) => {
      return {
        title: `${field.label}${field.thousandsUS ? "*" : ""}`,
        apiField: field.value,
        thousandsUS: field.thousandsUS,
      };
    }),
  ) as never[],
);

export function historicalHeaderLookup(report: string) {
  const headers: any = {
    CBS: CBSHeaders,
    CBF: CBFHeaders,
    SIS: SISHeaders,
    SIF: SIFHeaders,
  };
  return headers[report];
}

export function transformHistoricalData(
  historicalData: Historical[],
  headerData: HistoricalHeaders[],
) {
  return historicalData.map((data: Historical) => {
    const tableCellData: SimpleTableRow = {};
    headerData
      .map((header: HistoricalHeaders) => header.apiField)
      .forEach((apiField: HistoricalApiField) => {
        const value = data[apiField];
        tableCellData[apiField] = {
          value:
            value === "Northern Mariana Islands"
              ? `${value}**`
              : value
                ? value.toLocaleString()
                : value,
        };
      });
    return tableCellData;
  });
}
const REPORT_CLASSES: Record<string, string[]> = {
  CBS: ["N", "NM", "SM"],
  SIS: ["SB", "SA", "SL"],
};

const bridgeKey = (year: string, state: string) => `${year}|${state}`;

const yearOf = (date?: string) => date?.match(/\b(\d{4})\b/)?.[1];

const buildBridgeCounts = (
  banks: KeyValue[],
  report: string,
): Map<string, number> => {
  const counts = new Map<string, number>();
  const add = (key: string) => counts.set(key, (counts.get(key) ?? 0) + 1);
  const classes = REPORT_CLASSES[report] ?? [];
  banks.forEach((bank) => {
    const year = yearOf(bank?.ESTYMD);
    if (!year || !classes.includes(bank?.BKCLASS)) return;
    add(bridgeKey(year, "ALL"));
    if (bank?.STNAME) add(bridgeKey(year, bank.STNAME));
  });
  return counts;
};
const NEW_CHARTER_FIELDS: Record<string, string[]> = {
  CBS: ["New_Char"],
  SIS: ["newcount"],
};
const NATIONAL_STNAME = "All States and Territories";
const subtractBridgeEvents = (
  rows: Historical[],
  counts: Map<string, number>,
  report: string,
): Historical[] => {
  const fields = NEW_CHARTER_FIELDS[report] ?? [];
  if (counts.size === 0 || fields.length === 0) return rows;
  return rows.map((row: any) => {
    const state = row.STNAME === NATIONAL_STNAME ? "ALL" : row.STNAME;
    const n = counts.get(bridgeKey(row.YEAR, state)) ?? 0;
    if (!n) return row;
    const adjusted = { ...row };
    fields.forEach((field) => {
      if (typeof adjusted[field] === "number") {
        adjusted[field] = Math.max(0, adjusted[field] - n);
      }
    });
    return adjusted;
  });
};
export default function HistoricalBankData({
  appState,
  updateSort,
  updateEndDate,
}: Props) {
  //used to call the useEffect when location needs to reset to previous

  const [tableNote, setTableNote] = React.useState("");
  const [historicalData, setHistoricalData] = React.useState<Historical[]>([]);
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });
  const [endYear, setEndYear] = React.useState(0);
  const [hasMP, setHasMP] = React.useState(false);
  const [bridgeBanks, setBridgeBanks] = React.useState<KeyValue[]>([]);
  //combines static headers and headers based on lookup.
  const headerValues = [
    ...staticHeaders,
    ...historicalHeaderLookup(appState.filters.selectedReport),
  ].filter(
    (value, index, array) =>
      array.findIndex((arrayVal) => arrayVal.apiField === value.apiField) ===
      index,
  );

  const combinedDisplayFields =
    appState.displayFields && appState.fixedDisplayFields
      ? appState.displayFields.concat(appState.fixedDisplayFields)
      : [];

  const HistoricalHeadersMetaData = headerValues.filter((header) =>
    combinedDisplayFields.includes(header.apiField),
  );

  React.useEffect(() => {
    setTableNote("");
    return HistoricalHeadersMetaData.forEach((header) => {
      if (header.thousandsUS) {
        return setTableNote("* Numbers represent thousands of US Dollars");
      }
      if (header.specialNote) {
        return setTableNote(
          "** State level institution count is currently unavailable",
        );
      }
    });
  }, [HistoricalHeadersMetaData]);

  const HistoricalHeaders: TableHeader[][] = [
    HistoricalHeadersMetaData.map((header: HistoricalHeaders) => ({
      title: header.title,
      styles: {},
      colSpan: 1,
      apiField: header.apiField,
    })),
  ];

  const introParagraph = (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>{appHeaderTitle("historical")}</Grid>
      </Grid>
      <Typography variant="body2" id="historical-description">
        BankFind Suite's aggregate historical bank data is the annual summary of
        financial and structure data for all FDIC-insured institutions
        headquartered in a state, city or US territory. The data can be used to
        identify and analyze long term trends and to develop benchmarks to
        evaluate the current conditions of the banking and thrift industries.
        This annual data is available as of December 31 for the years 1934 to{" "}
        {endYear}
      </Typography>
      <LinkComponent appName={"HistoricalBankData"} />
    </>
  );

  //Sets end year based on API
  React.useEffect(() => {
    if (endYear === 0) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/summary?limit=1&offset=0&sort_by=YEAR&sort_order=DESC`,
        )
          .then((response) => {
            if (isMounted) {
              setEndYear(response.data[0].YEAR);
              updateEndDate(response?.data?.[0].YEAR?.toString());
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [endYear, updateEndDate]);

  React.useEffect(() => {
    if (appState.filters.endDate) {
      const updatedAppState = {
        ...appState,
        filters: {
          ...appState.filters,
          selectedReport: appState.filters.selectedReport.slice(0, -1),
          selectedStates:
            appState.filters.selectedStates.length > 0
              ? appState.filters.selectedStates
              : [undefined],
        },
      };
      const isStructureReport = appState.filters.selectedReport.endsWith("S");
      const bridgeUrl =
        `${ApiBaseUrl()}/institutions?filters=${encodeURIComponent("CHANGEC1:216")}` +
        `&fields=CERT,NAME,ESTYMD,STNAME,BKCLASS&limit=100`;

      let isMounted = true;
      (async () => {
        try {
          const [summary, bridge] = await Promise.all([
            fetchData(buildQueryUrl("/summary", updatedAppState)),
            isStructureReport
              ? fetchData(bridgeUrl).catch((error: Error) => {
                  console.error(error);
                  return { data: [] };
                })
              : Promise.resolve({ data: [] }),
          ]);
          if (!isMounted) return;
          setBridgeBanks(bridge?.data ?? []);
          const report = appState.filters.selectedReport;
          const counts = buildBridgeCounts(bridge?.data ?? [], report);
          const adjusted = subtractBridgeEvents(
            summary?.data ?? [],
            counts,
            report,
          );
          adjusted.forEach((row: any, i: number) => {
            if (row === summary.data[i]) return;
          });
          setHasMP(
            adjusted.some(
              (item: KeyValue) => item?.STNAME === "Northern Mariana Islands",
            ),
          );
          setHistoricalData(adjusted);
          setMetaData(summary.totals);
        } catch (error) {
          console.error(error);
        }
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState]);

  const historyAppState = {
    ...appState,
    filters: {
      ...appState.filters,
      selectedReport: appState.filters.selectedReport.slice(0, -1),
    },
  };

  const downloadHeaders: DownloadHeaders[] = HistoricalHeaders[0].map(
    (header) => ({
      label: `${header.title}`,
      key: `${header.apiField}`,
    }),
  );

  const apiData: apiDownloadProps[] = [
    {
      appState: historyAppState,
      fileName: `${fileNameFormat("Summary of Results")}_raw.csv`,
      apiPath: "summary",
      text: "Download Results Data - Raw",
    },
    {
      appState: historyAppState,
      fileName: `${fileNameFormat("Summary of Results")}_formatted.csv`,
      apiPath: "summary",
      text: "Download Results Data - Formatted",
      headers: downloadHeaders,
      transformData: (rows: KeyValue[]) =>
        subtractBridgeEvents(
          rows as Historical[],
          buildBridgeCounts(bridgeBanks, appState.filters.selectedReport),
          appState.filters.selectedReport,
        ) as any,
    },
  ];
  const footerMP = (
    <Typography className={classes.footer}>
      **AKA Commonwealth of the Northern Mariana Islands
    </Typography>
  );
  useInitialPageLoad("historical");
  return (
    <Root>
      <Breadcrumbs currentPageBreadCrumbTitle={"Annual Data Summaries"} />
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        searchForm={<HistoricalSearchForm endYear={endYear} />}
        expand={getExpandState(appState.ui.searchPanelExpand, true)}
      >
        <>
          <Typography
            component="div"
            id="multi-header-table-title"
            marginTop={-1}
          >
            <Box
              id="resultSummary"
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h2">
                {metaData.count.toLocaleString()}{" "}
                {metaData.count === 1 ? "Matching Result" : "Matching Results"}
              </Typography>
              <ApiDataDownload
                app="historical"
                apiData={apiData}
                appState={historyAppState}
              />
            </Box>
            <Divider style={{ marginBottom: "1rem" }} />
          </Typography>
          <ResultPagination count={metaData.count} hideBottomPagination>
            <div className={classes.tableStyles}>
              {tableNote !== "" ? (
                <Typography
                  variant="h4"
                  id="tableNote"
                  style={{ paddingBottom: 0 }}
                >
                  {tableNote}
                </Typography>
              ) : (
                ""
              )}
              <MultiHeaderTable
                sortProperties={useSortProperties(appState, updateSort)}
                tableHeaders={HistoricalHeaders}
                tableBody={
                  <SimpleTableBody
                    tableData={transformHistoricalData(
                      historicalData,
                      HistoricalHeadersMetaData,
                    )}
                    nonInteractive
                  />
                }
              />
            </div>
          </ResultPagination>
          {hasMP && footerMP}
        </>
      </AppBody>
    </Root>
  );
}
